// DesignPreviewCanvas.js - Fixed for OVH images with CORS
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import ColorPreviewCanvas from '../ColorPreviewImage/ColorPreviewCanvas';
import { getImageUrl, withCacheBust } from '../../utils/imageProxyUtils';

const DesignPreviewCanvas = forwardRef(({ 
  productImage, 
  designImage, 
  color, 
  metadata = null, 
  className = '', 
  style = {},
  onLoad = () => {},
  onError = () => {}
}, ref) => {
  const canvasRef = useRef(null);
  const [productLoaded, setProductLoaded] = useState(false);
  const [designLoaded, setDesignLoaded] = useState(false);
  const productCanvasRef = useRef(null);
  const designImgRef = useRef(null);
  const [currentColor, setCurrentColor] = useState(color);
  const [isDrawing, setIsDrawing] = useState(false);
  const animationFrameRef = useRef(null);
  const isApplyingRef = useRef(false);
  const drawTimeoutRef = useRef(null);
  const initialDrawDoneRef = useRef(false);
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [actualDesignImage, setActualDesignImage] = useState(null);

  useImperativeHandle(ref, () => ({
    applyColor: (newColor) => {
      if (isApplyingRef.current) return;
      isApplyingRef.current = true;
      
      if (productCanvasRef.current && newColor) {
        productCanvasRef.current.applyColor(newColor);
        setCurrentColor(newColor);
      }
      
      setTimeout(() => {
        isApplyingRef.current = false;
      }, 50);
    },
    reload: () => {
      setProductLoaded(false);
      setDesignLoaded(false);
      initialDrawDoneRef.current = false;
      setLoadError(false);
      setRetryCount(prev => prev + 1);
    },
    getCanvasRef: () => canvasRef.current
  }));

  // Process design image URL when prop changes
  useEffect(() => {
    if (designImage) {
      const url = getImageUrl(designImage);
      console.log('DesignPreviewCanvas - Processing design image:', url);
      setActualDesignImage(url);
      setDesignLoaded(false);
      setLoadError(false);
    }
  }, [designImage]);

  // Draw combined image
  const drawCombined = useCallback(() => {
    const productCanvasElement = productCanvasRef.current?.canvasRef?.current;
    
    if (!canvasRef.current || !productCanvasElement || !designImgRef.current) {
      return;
    }
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const designImg = designImgRef.current;
    
    // Set canvas dimensions to match product canvas
    if (canvas.width !== productCanvasElement.width || canvas.height !== productCanvasElement.height) {
      canvas.width = productCanvasElement.width;
      canvas.height = productCanvasElement.height;
    }
    
    // Cancel any pending draw
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    animationFrameRef.current = requestAnimationFrame(() => {
      setIsDrawing(true);
      
      // Clear with transparency
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      try {
        // Draw product image (already colored by ColorPreviewCanvas)
        if (productCanvasElement && (productCanvasElement.complete || productCanvasElement.naturalWidth > 0 || productCanvasElement.width > 0)) {
          ctx.drawImage(productCanvasElement, 0, 0, canvas.width, canvas.height);
        }
        
        // Draw design overlay with metadata positioning if available
        if (designImg && designImg.complete && designImg.naturalWidth > 0) {
          if (metadata && metadata.length > 0) {
            // Draw all designs from metadata
            for (const meta of metadata) {
              if (meta && meta.originalWidth && meta.originalHeight) {
                // Calculate scaling based on a reference width/height (500px as in editor)
                const scaleX = canvas.width / 500;
                const scaleY = canvas.height / 500;
                
                // Calculate position and dimensions based on metadata
                const centerX = meta.x * scaleX + (meta.originalWidth * (meta.scaleX || 1) * scaleX) / 2;
                const centerY = meta.y * scaleY + (meta.originalHeight * (meta.scaleY || 1) * scaleY) / 2;
                const width = meta.originalWidth * (meta.scaleX || 1) * scaleX;
                const height = meta.originalHeight * (meta.scaleY || 1) * scaleY;
                const rotation = meta.rotation || 0;
                
                ctx.save();
                ctx.translate(centerX, centerY);
                ctx.rotate(rotation * Math.PI / 180);
                ctx.drawImage(designImg, -width / 2, -height / 2, width, height);
                ctx.restore();
              }
            }
          } else {
            // No metadata - draw one image centered with proper scaling
            const maxWidth = canvas.width * 0.8;
            const maxHeight = canvas.height * 0.8;
            let drawWidth = designImg.width;
            let drawHeight = designImg.height;
            
            // Scale to fit within max dimensions while maintaining aspect ratio
            if (drawWidth > maxWidth) {
              const ratio = maxWidth / drawWidth;
              drawWidth = maxWidth;
              drawHeight = drawHeight * ratio;
            }
            if (drawHeight > maxHeight) {
              const ratio = maxHeight / drawHeight;
              drawHeight = maxHeight;
              drawWidth = drawWidth * ratio;
            }
            
            const x = (canvas.width - drawWidth) / 2;
            const y = (canvas.height - drawHeight) / 2;
            ctx.drawImage(designImg, x, y, drawWidth, drawHeight);
          }
        }
      } catch (err) {
        console.error("Error drawing combined canvas:", err);
      }
      
      setIsDrawing(false);
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    });
  }, [metadata]);

  // Reapply color to product canvas when color changes
  useEffect(() => {
    if (currentColor && productLoaded && productCanvasRef.current && productCanvasRef.current.applyColor) {
      productCanvasRef.current.applyColor(currentColor);
    }
  }, [currentColor, productLoaded]);

  // Redraw combined image when product canvas updates
  useEffect(() => {
    if (productLoaded && designLoaded && productCanvasRef.current?.canvasRef?.current && !isDrawing && initialDrawDoneRef.current) {
      if (drawTimeoutRef.current) {
        clearTimeout(drawTimeoutRef.current);
      }
      drawTimeoutRef.current = setTimeout(() => {
        drawCombined();
        drawTimeoutRef.current = null;
      }, 16);
      return () => {
        if (drawTimeoutRef.current) {
          clearTimeout(drawTimeoutRef.current);
        }
      };
    }
  }, [productLoaded, designLoaded, drawCombined, isDrawing, currentColor]);

  const handleProductLoad = useCallback(() => {
    setProductLoaded(true);
    if (currentColor && productCanvasRef.current?.applyColor) {
      productCanvasRef.current.applyColor(currentColor);
    }
    onLoad();
  }, [currentColor, onLoad]);

  const handleProductError = useCallback((err) => {
    console.error("Product image load error:", err);
    setLoadError(true);
    onError(err);
  }, [onError]);

  const handleDesignLoad = useCallback(() => {
    console.log('Design image loaded successfully');
    setDesignLoaded(true);
  }, []);

  const handleDesignError = useCallback((err) => {
    console.error("Design image load error:", err);
    if (retryCount < 2) {
      console.log(`Retrying design image load (attempt ${retryCount + 1})...`);
      setRetryCount(prev => prev + 1);
      // Force reload with cache busting
      if (designImgRef.current && actualDesignImage) {
        setTimeout(() => {
          designImgRef.current.src = withCacheBust(actualDesignImage);
        }, 500);
      }
    } else {
      setLoadError(true);
      onError(err);
    }
  }, [actualDesignImage, retryCount, onError]);

  // Reload image when retry count changes
  useEffect(() => {
    if (actualDesignImage && retryCount > 0 && designImgRef.current) {
      designImgRef.current.src = withCacheBust(actualDesignImage);
    }
  }, [actualDesignImage, retryCount]);

  // Initial draw when both are loaded
  useEffect(() => {
    if (productLoaded && designLoaded && !isDrawing && !initialDrawDoneRef.current) {
      initialDrawDoneRef.current = true;
      if (drawTimeoutRef.current) {
        clearTimeout(drawTimeoutRef.current);
      }
      drawTimeoutRef.current = setTimeout(() => {
        drawCombined();
        drawTimeoutRef.current = null;
      }, 100);
      return () => {
        if (drawTimeoutRef.current) {
          clearTimeout(drawTimeoutRef.current);
        }
      };
    }
  }, [productLoaded, designLoaded, drawCombined, isDrawing]);

  // Respond to external color prop changes
  useEffect(() => {
    if (color !== currentColor && color) {
      setCurrentColor(color);
    }
  }, [color, currentColor]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (drawTimeoutRef.current) {
        clearTimeout(drawTimeoutRef.current);
      }
    };
  }, []);

  // Get the product image URL
  const productImageUrl = getImageUrl(productImage);
  const designImageUrl = actualDesignImage;

  if (!productImageUrl) {
    return (
      <div className={`image-fallback-large ${className}`} style={style}>
        <span>No product image</span>
      </div>
    );
  }
  
  if (!designImageUrl) {
    return (
      <div className={`image-fallback-large ${className}`} style={style}>
        <span>No design image</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`image-fallback-large ${className}`} style={style}>
        <span>⚠️ Unable to load image</span>
        <button 
          onClick={() => {
            setLoadError(false);
            setRetryCount(0);
            setDesignLoaded(false);
            if (designImgRef.current && designImageUrl) {
              designImgRef.current.src = withCacheBust(designImageUrl);
            }
          }}
          style={{ marginTop: '10px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Hidden ColorPreviewCanvas for product - handles color changes smoothly */}
      <div style={{ display: 'none' }}>
        <ColorPreviewCanvas
          ref={productCanvasRef}
          imageUrl={productImageUrl}
          color={currentColor}
          onLoad={handleProductLoad}
          onError={handleProductError}
        />
      </div>
      
      {/* Hidden design image for loading */}
      <img
        ref={designImgRef}
        src={designImageUrl}
        alt="design"
        style={{ display: 'none' }}
        onLoad={handleDesignLoad}
        onError={handleDesignError}
        crossOrigin="Anonymous"
      />
      
      {/* Combined canvas */}
      <canvas
        ref={canvasRef}
        className={className}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          ...style
        }}
      />
    </div>
  );
});

DesignPreviewCanvas.displayName = 'DesignPreviewCanvas';

export default DesignPreviewCanvas;
